import { ChevronLeft, ChevronRight, MoreVertical } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useGetDispatchGraphDataMutation } from "../dispatchApiSlice";

interface DispatchGraphData {
  currentPeriod: string;
  totalTrips: number;
  totalDistance: number;
  totalEarnings: number;
  monthlyData: MonthlyData[];
  dailyData: DailyData[];
}

interface MonthlyData {
  month: string;
  tripCount: number;
  totalEarnings: number;
}

interface DailyData {
  date: string;
  tripCount: number;
  totalEarnings: number;
}

interface DispatchGraphTabProps {
  id?: string;
  idType: string;
}

const DispatchGraphTab: React.FC<DispatchGraphTabProps> = ({ id, idType }) => {
  const [getDispatchGraphData, { isLoading }] =
    useGetDispatchGraphDataMutation();

  const [graphData, setGraphData] = useState<DispatchGraphData | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isViewMenuOpen, setIsViewMenuOpen] = useState(false);
  const [viewType, setViewType] = useState<"daily" | "monthly">("daily");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const fetchData = useCallback(async () => {
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();

    const result = await getDispatchGraphData({
      vehicleId: idType === "vehicle" ? id : undefined,
      driverId: idType === "driver" ? id : undefined,
      year,
      month,
    });
    const resultData = result?.data?.data;
    if (resultData) {
      setGraphData(resultData);
    }
  }, [id, currentDate, getDispatchGraphData, idType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setCurrentDate(date);
      setShowDatePicker(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const scrollLeftHandler = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRightHandler = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  const currentData =
    viewType === "daily" ? graphData?.dailyData : graphData?.monthlyData;

  let maxTrips = 4;
  if (currentData && currentData.length > 0) {
    maxTrips = Math.max(...currentData.map((item) => item.tripCount));
  }

  const formatDate = (dateString: string) => {
    const parts = dateString.split("-");
    const monthIndex = parseInt(parts[0]) - 1;
    return `${monthNames[monthIndex]?.slice(0, 3)} ${parts[1]}`;
  };

  const formatMonth = (monthString: string) => {
    return monthString.substring(0, 3);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getCurrentPeriod = () => {
    if (viewType === "daily") {
      return `${
        monthNames[currentDate.getMonth()]
      } ${currentDate.getFullYear()}`;
    }
    return currentDate.getFullYear().toString();
  };

  const displayPeriod = graphData?.currentPeriod || getCurrentPeriod();

  if (isLoading) {
    return <div className="p-6 bg-white rounded-lg">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg p-4 sm:p-6 max-w-full lg:max-w-[83vw]">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-12">
        <div className="flex items-center gap-4">
          <div>
            <div className="text-gray-500 text-sm mb-1">Total Trips</div>
            <div className="text-4xl font-semibold">
              {graphData?.totalTrips || 0}
            </div>
          </div>

          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md flex items-center gap-2 hover:bg-gray-50"
            >
              {displayPeriod}
              <ChevronRight className="w-4 h-4 transform rotate-90" />
            </button>
            {showDatePicker && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-white">
                <DatePicker
                  selected={currentDate}
                  onChange={handleDateChange}
                  dateFormat={viewType === "daily" ? "MMMM yyyy" : "yyyy"}
                  showMonthYearPicker={viewType === "daily"}
                  showYearPicker={viewType === "monthly"}
                  inline
                  calendarClassName="shadow-lg border border-gray-200 rounded-lg"
                />
              </div>
            )}
          </div>
        </div>

        {/* View Type Menu */}
        <div className="relative">
          <button
            onClick={() => setIsViewMenuOpen(!isViewMenuOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <MoreVertical className="w-5 h-5 text-gray-600" />
          </button>

          {isViewMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <button
                onClick={() => {
                  setViewType("daily");
                  setIsViewMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 rounded-t-lg ${
                  viewType === "daily" ? "bg-gray-100 font-medium" : ""
                }`}
              >
                Daily
              </button>
              <button
                onClick={() => {
                  setViewType("monthly");
                  setIsViewMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 rounded-b-lg ${
                  viewType === "monthly" ? "bg-gray-100 font-medium" : ""
                }`}
              >
                Monthly
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chart with Navigation */}
      <div className="relative">
        {/* Left Arrow */}
        <button
          onClick={scrollLeftHandler}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>

        {/* Right Arrow */}
        <button
          onClick={scrollRightHandler}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>

        {/* Y-axis label */}
        <div className="absolute -left-2 top-0 text-xs text-gray-400">
          {maxTrips}
        </div>

        {/* Scrollable Container */}
        <div
          ref={scrollContainerRef}
          className="overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing px-12"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {/* Bars Container */}
          <div className="flex items-end gap-4 h-80 pt-8 pb-12">
            {currentData?.map((item, index) => {
              const heightPercentage =
                maxTrips > 0 ? (item.tripCount / maxTrips) * 100 : 0;
              const isHighlighted = item.tripCount === maxTrips && maxTrips > 0;

              return (
                <div
                  key={index}
                  className="flex flex-col items-center group relative flex-shrink-0"
                  style={{ width: "40px", height: "100%" }}
                >
                  <div className="flex-1 flex items-end w-full">
                    {/* Bar */}
                    <div
                      className={`w-full rounded-t-md transition-all duration-200 ${
                        isHighlighted ? "bg-[#a8b964]" : "bg-[#e8ead5]"
                      } group-hover:opacity-80 cursor-pointer`}
                      style={{
                        height: `${heightPercentage}%`,
                        minHeight: item.tripCount > 0 ? "8px" : "4px",
                      }}
                    >
                      {/* Tooltip */}
                      <div className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-20 pointer-events-none">
                        <div className="font-semibold">
                          {item.tripCount} trips
                        </div>
                        <div>{formatCurrency(item.totalEarnings)}</div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>

                  {/* Date/Month label */}
                  <div className="text-xs text-gray-400 mt-4 whitespace-nowrap">
                    {viewType === "daily"
                      ? formatDate((item as DailyData).date)
                      : formatMonth((item as MonthlyData).month)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default DispatchGraphTab;
